import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { useSession } from '@supabase/auth-helpers-react';
import { useEffect, useMemo } from 'react';
import {
  getPerfRequestHeaders,
  isPerfDiagnosticsEnabled,
  logPerfEvent,
  measurePayloadBytes,
  parseServerTimingHeader,
  roundPerfMs,
  storePendingRoutePerf,
} from '../perfDiagnostics';
import { supabase } from '../supabaseClient';

type PerfMetadata = {
  startedAtMs: number;
  requestLabel: string;
};

type StoredTokenResult = {
  token: string | null;
  source: 'supabase-v2' | 'none';
};

type ActiveAccessTokenResult = {
  token: string | null;
  source: StoredTokenResult['source'];
  sessionError?: ReturnType<typeof getErrorDetails>;
};

const LOGIN_FLOW_MARKER = 'intellerview.login.justSucceeded';

const getErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const maybeError = error as { name?: unknown; message?: unknown };
    return {
      name: typeof maybeError.name === 'string' ? maybeError.name : undefined,
      message: typeof maybeError.message === 'string' ? maybeError.message : String(error),
    };
  }

  return {
    message: String(error),
  };
};

const buildRequestLabel = (config: { method?: string; url?: string }): string => {
  const method = (config.method || 'get').toUpperCase();
  const url = config.url || '';
  return `${method} ${url}`;
};

const attachPerfInterceptors = (instance: AxiosInstance) => {
  const requestId = instance.interceptors.request.use((config) => {
    const instrumentedConfig = config as typeof config & { metadata?: PerfMetadata };
    instrumentedConfig.metadata = {
      startedAtMs: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      requestLabel: buildRequestLabel(config),
    };

    const perfHeaders = getPerfRequestHeaders();
    if (perfHeaders) {
      config.headers = config.headers || {};
      Object.assign(config.headers, perfHeaders);
    }

    return config;
  });

  const responseId = instance.interceptors.response.use(
    (response) => {
      if (!isPerfDiagnosticsEnabled()) return response;

      const config = response.config as typeof response.config & { metadata?: PerfMetadata };
      const startedAtMs = config.metadata?.startedAtMs;
      const requestLabel = config.metadata?.requestLabel || buildRequestLabel(response.config);
      const durationMs =
        typeof startedAtMs === 'number'
          ? roundPerfMs((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs)
          : null;
      const serverTiming = parseServerTimingHeader(
        typeof response.headers?.['server-timing'] === 'string' ? response.headers['server-timing'] : null
      );
      const payloadBytes = measurePayloadBytes(response.data);

      logPerfEvent('request_complete', {
        request: requestLabel,
        status: response.status,
        durationMs,
        serverTiming,
        payloadBytes,
      });

      if (typeof response.data?.next_url === 'string' && response.data.next_url.trim().length > 0 && durationMs !== null) {
        storePendingRoutePerf({
          path: response.data.next_url,
          source: requestLabel,
          responseEndedAtMs: typeof performance !== 'undefined' ? performance.now() : Date.now(),
          requestDurationMs: durationMs,
          serverTiming,
          payloadBytes,
        });
      }

      return response;
    },
    (error) => {
      if (isPerfDiagnosticsEnabled()) {
        const config = error?.config as { metadata?: PerfMetadata; method?: string; url?: string } | undefined;
        const startedAtMs = config?.metadata?.startedAtMs;
        const requestLabel = config?.metadata?.requestLabel || buildRequestLabel(config || {});
        const durationMs =
          typeof startedAtMs === 'number'
            ? roundPerfMs((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs)
            : null;
        const serverTiming = parseServerTimingHeader(
          typeof error?.response?.headers?.['server-timing'] === 'string'
            ? error.response.headers['server-timing']
            : null
        );

        logPerfEvent('request_error', {
          request: requestLabel,
          status: error?.response?.status || null,
          durationMs,
          serverTiming,
          message: error?.message || 'Request failed',
        });
      }

      return Promise.reject(error);
    }
  );

  return { requestId, responseId };
};

const readStoredTokenValue = (tokenItem: string | null): string | null => {
  if (!tokenItem) return null;
  try {
    const parsed = JSON.parse(tokenItem);
    return parsed.access_token || parsed.currentSession?.access_token || null;
  } catch {
    return null;
  }
};

const getStoredToken = (): StoredTokenResult => {
  const storageAreas = [localStorage, sessionStorage];
  for (const storage of storageAreas) {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;

      const token = readStoredTokenValue(storage.getItem(key));
      if (token) {
        return { token, source: 'supabase-v2' };
      }
    }
  }

  return { token: null, source: 'none' };
};

const resolveActiveAccessToken = async (): Promise<ActiveAccessTokenResult> => {
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      return { token: data.session.access_token, source: 'supabase-v2' };
    }
  } catch (error) {
    const storedToken = getStoredToken();
    return {
      ...storedToken,
      sessionError: getErrorDetails(error),
    };
  }

  return getStoredToken();
};

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

attachPerfInterceptors(api);

// Global interceptor for non-hook usage (fallback to storage)
api.interceptors.request.use(async (config) => {
  const authToken = await resolveActiveAccessToken();
  const token = authToken.token;
  const isFirstRequestAfterLogin = sessionStorage.getItem(LOGIN_FLOW_MARKER) === '1';
  if (isFirstRequestAfterLogin) {
    sessionStorage.removeItem(LOGIN_FLOW_MARKER);
    console.info('[LOGIN_FLOW] first API call after login', {
      tokenSource: authToken.source,
      hasToken: Boolean(token),
      sessionRetrievalError: authToken.sessionError || null,
    });
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.info('Global api auth token resolved', {
      tokenSource: authToken.source,
      hasToken: true,
    });
    return config;
  }
  console.warn('No token found for global api request', {
    sessionRetrievalError: authToken.sessionError || null,
    tokenSource: authToken.source,
    hasToken: false,
  });
  return config;
}, (error) => Promise.reject(error));

// Hook for components (reactive to session changes)
export const useApi = () => {
  const session = useSession();

  const instance = useMemo(() => axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
    headers: {
      'Content-Type': 'application/json',
    },
  }), []); // Memoize instance to avoid recreating on renders

  useEffect(() => {
    const perfInterceptors = attachPerfInterceptors(instance);
    const requestIntercept = instance.interceptors.request.use(async (config) => {
      let token = session?.access_token;
      let tokenSource: StoredTokenResult['source'] = token ? 'supabase-v2' : 'none';
      let sessionRetrievalError: ReturnType<typeof getErrorDetails> | null = null;
      if (!token) {
        const authToken = await resolveActiveAccessToken();
        token = authToken.token;
        tokenSource = authToken.source;
        sessionRetrievalError = authToken.sessionError || null;
      }

      const isFirstRequestAfterLogin = sessionStorage.getItem(LOGIN_FLOW_MARKER) === '1';
      if (isFirstRequestAfterLogin) {
        sessionStorage.removeItem(LOGIN_FLOW_MARKER);
        console.info('[LOGIN_FLOW] first API call after login', {
          tokenSource,
          hasToken: Boolean(token),
          sessionRetrievalError,
        });
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.info('useApi auth token resolved', {
          tokenSource,
          hasToken: true,
        });
      } else {
        console.warn('No token found in useApi', {
          sessionRetrievalError,
          tokenSource,
          hasToken: false,
        });
      }
      return config;
    }, (error) => Promise.reject(error));

    return () => {
      instance.interceptors.request.eject(requestIntercept); // Clean up on unmount/session change
      instance.interceptors.request.eject(perfInterceptors.requestId);
      instance.interceptors.response.eject(perfInterceptors.responseId);
    };
  }, [session, instance]); // React to session changes

  return instance;
};

export type CommerceCheckoutMode = 'PAY_NOW' | 'REQUEST_INVOICE';
export type CommerceOfferingCategory = 'CREDIT_PACK' | 'SIMULATION_BUILD';
export type CommerceBuildType = 'STANDARD' | 'SEMI_EXCLUSIVE' | 'EXCLUSIVE_ENTERPRISE' | null;
export type CommercePaymentStatus =
  | 'PENDING'
  | 'INVOICE_REQUESTED'
  | 'INVOICED'
  | 'PAID'
  | 'FAILED'
  | 'CANCELLED';
export type CommerceFulfillmentStatus =
  | 'PENDING'
  | 'AWAITING_BILLING'
  | 'AWAITING_PAYMENT'
  | 'AWAITING_SCOPING'
  | 'IN_BUILD'
  | 'FULFILLED'
  | 'CANCELLED';
export type CommerceBuildRequestStatus =
  | 'PENDING'
  | 'INVOICE_REQUESTED'
  | 'AWAITING_BILLING'
  | 'PAID'
  | 'SCOPING'
  | 'IN_BUILD'
  | 'DELIVERED'
  | 'CANCELLED';

export type CommerceOffering = {
  id: string;
  code: string;
  name: string;
  category: CommerceOfferingCategory;
  buildType: CommerceBuildType;
  fulfillmentType: 'CREDIT_ALLOCATION' | 'BUILD_REQUEST';
  priceZar: string;
  currency: 'ZAR';
  credits: number | null;
  isActive: boolean;
  isSelfServe: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type CommerceBillingProfile = {
  id: string;
  companyId: string;
  legalEntityName: string;
  billingAddress: string | Record<string, unknown>;
  accountsPayableEmail: string;
  registrationNumber: string | null;
  vatNumber: string | null;
  poNumber: string | null;
  costCentreNumber: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CommerceBillingProfileInput = {
  legalEntityName: string;
  billingAddress: string;
  accountsPayableEmail: string;
  registrationNumber?: string;
  vatNumber?: string;
  poNumber?: string;
  costCentreNumber?: string;
};

export type CommerceOrderPaymentResponse = {
  orderId: string;
  merchantReference: string;
  payfastUrl: string;
  formFields: Record<string, string>;
  paymentStatus: CommercePaymentStatus;
  fulfillmentStatus: CommerceFulfillmentStatus;
};

export type PayFastLiveTestStatus = {
  enabled: boolean;
  appOfferingCode: string;
  landingOfferingCode: string;
  amountZar: string;
};

export type CommerceOrderInvoiceResponse = {
  orderId: string;
  paymentStatus: CommercePaymentStatus;
  fulfillmentStatus: CommerceFulfillmentStatus;
  message: string;
};

export type CommerceOrderCreateInput = {
  offeringCode: string;
  checkoutMode: CommerceCheckoutMode;
  creditQuantity?: number;
  buildRequest?: {
    roleName: string;
    notes?: string;
  };
};

export type CommerceOrderStatus = {
  orderId: string;
  offeringCode: string;
  offeringName: string;
  paymentStatus: CommercePaymentStatus;
  fulfillmentStatus: CommerceFulfillmentStatus;
  paidAt: string | null;
  xeroInvoiceId: string | null;
  xeroInvoiceNumber: string | null;
  buildRequestId: string | null;
  buildRequestStatus: CommerceBuildRequestStatus | null;
  companyCreditBalance:
    | {
        balance: number;
        reservedBalance: number;
        availableBalance: number;
      }
    | null;
};

export type CommerceBuildRequest = {
  id: string;
  roleName: string;
  notes: string | null;
  status: CommerceBuildRequestStatus;
  buildType: CommerceBuildType;
  source: 'APP' | 'LANDING';
  requesterName: string;
  requesterEmail: string;
  requesterCompanyName: string;
  createdAt: string;
  updatedAt: string;
  company: {
    id: string;
    name: string;
  } | null;
  offering: {
    id: string;
    code: string;
    name: string;
    priceZar: string;
  };
  paymentOrder: {
    id: string;
    paymentStatus: CommercePaymentStatus;
    fulfillmentStatus: CommerceFulfillmentStatus;
    paidAt: string | null;
    itemNameSnapshot: string;
  } | null;
};

export type CommerceCreditBalance = {
  balance: number;
  reservedBalance: number;
  availableBalance: number;
};

const commerceClient = (client?: AxiosInstance) => client ?? api;

export const fetchCreditPacks = async (client?: AxiosInstance): Promise<CommerceOffering[]> => {
  const response = await commerceClient(client).get<{ offerings: CommerceOffering[] }>('/commerce/credit-packs');
  return Array.isArray(response.data?.offerings) ? response.data.offerings : [];
};

export const fetchBuildOfferings = async (client?: AxiosInstance): Promise<CommerceOffering[]> => {
  const response = await commerceClient(client).get<{ offerings: CommerceOffering[] }>('/commerce/build-offerings');
  return Array.isArray(response.data?.offerings) ? response.data.offerings : [];
};

export const fetchBillingProfile = async (client?: AxiosInstance): Promise<CommerceBillingProfile | null> => {
  try {
    const response = await commerceClient(client).get<{ profile: CommerceBillingProfile | null }>('/commerce/billing-profile');
    return response.data?.profile ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const saveBillingProfile = async (
  input: CommerceBillingProfileInput,
  client?: AxiosInstance
): Promise<CommerceBillingProfile> => {
  const response = await commerceClient(client).put<{ profile: CommerceBillingProfile }>('/commerce/billing-profile', input);
  return response.data.profile;
};

export const createCommerceOrder = async (
  input: CommerceOrderCreateInput,
  client?: AxiosInstance
): Promise<CommerceOrderPaymentResponse | CommerceOrderInvoiceResponse> => {
  const response = await commerceClient(client).post<CommerceOrderPaymentResponse | CommerceOrderInvoiceResponse>(
    '/commerce/orders',
    input
  );
  return response.data;
};

export const fetchCommerceOrderStatus = async (
  orderId: string,
  client?: AxiosInstance
): Promise<CommerceOrderStatus> => {
  const response = await commerceClient(client).get<CommerceOrderStatus>(`/commerce/orders/${orderId}/status`);
  return response.data;
};

export const fetchCommerceBuildRequests = async (client?: AxiosInstance): Promise<CommerceBuildRequest[]> => {
  const response = await commerceClient(client).get<{ buildRequests: CommerceBuildRequest[] }>('/commerce/build-requests');
  return Array.isArray(response.data?.buildRequests) ? response.data.buildRequests : [];
};

export const fetchAdminCommerceBuildRequests = async (client?: AxiosInstance): Promise<CommerceBuildRequest[]> => {
  const response = await commerceClient(client).get<{ buildRequests: CommerceBuildRequest[] }>('/commerce/admin/build-requests');
  return Array.isArray(response.data?.buildRequests) ? response.data.buildRequests : [];
};

export const updateAdminCommerceBuildRequestStatus = async (
  buildRequestId: string,
  status: Extract<CommerceBuildRequestStatus, 'PAID' | 'IN_BUILD' | 'DELIVERED'>,
  client?: AxiosInstance
): Promise<CommerceBuildRequest> => {
  const response = await commerceClient(client).patch<{ buildRequest: CommerceBuildRequest }>(
    `/commerce/admin/build-requests/${buildRequestId}/status`,
    { status }
  );
  return response.data.buildRequest;
};

export const fetchPayFastLiveTestStatus = async (client?: AxiosInstance): Promise<PayFastLiveTestStatus> => {
  const response = await commerceClient(client).get<PayFastLiveTestStatus>('/commerce/admin/payfast-live-test/status');
  return response.data;
};

export const createPayFastLiveTestAppOrder = async (
  client?: AxiosInstance
): Promise<CommerceOrderPaymentResponse> => {
  const response = await commerceClient(client).post<CommerceOrderPaymentResponse>('/commerce/admin/payfast-live-test/app');
  return response.data;
};

export const createPayFastLiveTestLandingOrder = async (
  client?: AxiosInstance
): Promise<CommerceOrderPaymentResponse> => {
  const response = await commerceClient(client).post<CommerceOrderPaymentResponse>(
    '/commerce/admin/payfast-live-test/landing'
  );
  return response.data;
};

export const fetchCreditBalance = async (client?: AxiosInstance): Promise<CommerceCreditBalance> => {
  const response = await commerceClient(client).get<CommerceCreditBalance>('/commerce/credits/balance');
  return response.data;
};

export default api;
